/**
 * Singleton tooltip system (#268).
 *
 * Any element with `data-tooltip="text"` gets a styled tooltip on
 * hover or keyboard focus. No wrapping element required — installs
 * once at module load, then handles every matching element via
 * document-level event delegation.
 *
 * Positioning follows the cursor — default is right + slightly below,
 * flipping to left of cursor when it would clip the right edge of the
 * viewport (and similarly above when clipping the bottom). For keyboard
 * focus there's no cursor, so the tooltip anchors to the target rect's
 * bottom-right corner.
 *
 * Usage:
 *
 *   <button class="btn-icon" data-tooltip="Refresh" aria-label="Refresh">
 *     <svg-icon icon="luboo:refresh"></svg-icon>
 *   </button>
 *
 * Always pair with `aria-label` for screen readers — `data-tooltip` is
 * for sighted users only.
 *
 * Drop the native `title=""` on the same element so browsers don't
 * race our styled tooltip with their default delayed one.
 */

const SHOW_DELAY_MS = 200;
const HIDE_DELAY_MS = 0;
const VIEWPORT_PADDING = 8;
// Cursor-relative offset. Default is right+slightly-below; the cursor
// itself shouldn't sit on top of the tooltip, hence the +X breathing room.
const CURSOR_OFFSET_X = 16;
const CURSOR_OFFSET_Y = 14;

class LubooTooltip {
  static _instance = null;

  static install() {
    if (typeof window === 'undefined') return null;
    if (!LubooTooltip._instance) LubooTooltip._instance = new LubooTooltip();
    return LubooTooltip._instance;
  }

  constructor() {
    this._tooltipEl = null;
    this._currentTarget = null;
    this._showTimer = null;
    this._hideTimer = null;
    // Last pointer position; null for focus-triggered tooltips (keyboard
    // nav has no cursor — anchor to the target rect's bottom-right corner).
    this._lastPointer = null;
    this._moveRaf = null;
    this._onOver = this._onOver.bind(this);
    this._onOut = this._onOut.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onFocusIn = this._onFocusIn.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);

    document.addEventListener('mouseover', this._onOver, { capture: true });
    document.addEventListener('mouseout', this._onOut, { capture: true });
    document.addEventListener('mousemove', this._onMove, { capture: true, passive: true });
    document.addEventListener('focusin', this._onFocusIn);
    document.addEventListener('focusout', this._onFocusOut);
    document.addEventListener('scroll', this._onScroll, { capture: true, passive: true });
    // Click / press dismisses immediately. Without this, hovering an info
    // button then opening a dialog leaves the tooltip stuck on screen —
    // mouseout often never fires (top-layer modal), and closing the dialog
    // restores focus to the button which would re-show via focusin.
    // Listen to both pointerdown (real pointer) and click (covers keyboard
    // activation + synthetic clicks that skip pointer events).
    document.addEventListener('pointerdown', this._onPointerDown, { capture: true });
    document.addEventListener('click', this._onPointerDown, { capture: true });
  }

  /**
   * Find a tooltip target. Matches `[data-tooltip]` OR `[title]`, and
   * promotes `title` → `data-tooltip` on first encounter so:
   *   - Browsers don't double the popup (their delayed default + our styled one)
   *   - All existing `<button title="…">` markup gets styled tooltips for
   *     free, without an 80-attribute markup sweep
   * Accessibility preserved: if the element has no aria-label /
   * aria-labelledby, we promote the title text to aria-label so screen
   * readers still announce it.
   */
  _resolveTarget(eventTarget) {
    if (!eventTarget || !eventTarget.closest) return null;
    const target = eventTarget.closest('[data-tooltip], [title]');
    if (!target) return null;
    if (!target.hasAttribute('data-tooltip') && target.hasAttribute('title')) {
      const titleText = target.getAttribute('title');
      if (titleText) {
        target.setAttribute('data-tooltip', titleText);
        if (!target.hasAttribute('aria-label') && !target.hasAttribute('aria-labelledby')) {
          target.setAttribute('aria-label', titleText);
        }
      }
      target.removeAttribute('title');
    }
    return target;
  }

  _onOver(e) {
    this._lastPointer = { x: e.clientX, y: e.clientY };
    const target = this._resolveTarget(e.target);
    if (!target || target === this._currentTarget) return;
    this._scheduleShow(target);
  }

  _onOut(e) {
    const target = this._resolveTarget(e.target);
    if (!target) return;
    if (e.relatedTarget && target.contains(e.relatedTarget)) return;
    this._hide();
  }

  _onMove(e) {
    this._lastPointer = { x: e.clientX, y: e.clientY };
    if (!this._currentTarget) return;
    if (!this._tooltipEl?.classList.contains('is-visible')) return;
    // Throttle to once-per-frame; pointer-events fire ~60-1000 times/sec.
    if (this._moveRaf) return;
    this._moveRaf = window.requestAnimationFrame(() => {
      this._moveRaf = null;
      if (this._currentTarget && this._tooltipEl?.classList.contains('is-visible')) {
        this._position();
      }
    });
  }

  _onFocusIn(e) {
    const target = this._resolveTarget(e.target);
    if (!target) return;
    // Only show for keyboard focus. Mouse-driven focus (e.g. click, or
    // dialog close restoring focus to the trigger button) uses
    // :focus-visible === false in modern browsers — re-showing the
    // tooltip there leaves "How to embed" floating after the dialog closes.
    if (typeof target.matches === 'function' && !target.matches(':focus-visible')) {
      return;
    }
    // Focus = keyboard nav, no cursor — clear pointer so positioning
    // anchors to the target's rect instead of stale mouse coords.
    this._lastPointer = null;
    this._scheduleShow(target, { instant: true });
  }

  _onFocusOut(e) {
    const target = this._resolveTarget(e.target);
    if (!target) return;
    this._hide();
  }

  _onScroll() {
    if (this._currentTarget) this._hide();
  }

  _onPointerDown() {
    this._hide();
  }

  _scheduleShow(target, { instant = false } = {}) {
    this._clearTimers();
    if (instant) {
      this._show(target);
    } else {
      this._showTimer = window.setTimeout(() => this._show(target), SHOW_DELAY_MS);
    }
  }

  _show(target) {
    if (!target.isConnected) return;
    const text = target.getAttribute('data-tooltip');
    if (!text) return;
    this._currentTarget = target;
    if (!this._tooltipEl) this._build();
    this._tooltipEl.textContent = text;
    this._position();
    this._tooltipEl.classList.add('is-visible');
  }

  _hide() {
    this._clearTimers();
    this._currentTarget = null;
    if (this._moveRaf) {
      window.cancelAnimationFrame(this._moveRaf);
      this._moveRaf = null;
    }
    if (this._tooltipEl) this._tooltipEl.classList.remove('is-visible');
  }

  _clearTimers() {
    if (this._showTimer) {
      window.clearTimeout(this._showTimer);
      this._showTimer = null;
    }
    if (this._hideTimer) {
      window.clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }
  }

  _build() {
    this._tooltipEl = document.createElement('div');
    this._tooltipEl.className = 'luboo-tooltip';
    this._tooltipEl.setAttribute('role', 'tooltip');
    document.body.appendChild(this._tooltipEl);
  }

  /**
   * Position the tooltip relative to the cursor (or, for keyboard-focus
   * targets without a cursor, the target's bottom-right corner).
   * Default: right and slightly below the anchor. Flips horizontally if
   * it would clip the right edge of the viewport, vertically if it would
   * clip the bottom. Final viewport clamp prevents tiny-screen escapes.
   */
  _position() {
    const tt = this._tooltipEl;
    // Reset to (0,0) so the bounding-rect measurement isn't biased by a
    // previous large left/top putting the tooltip in a different layout.
    tt.style.left = '0px';
    tt.style.top = '0px';
    const ttRect = tt.getBoundingClientRect();

    let anchorX;
    let anchorY;
    if (this._lastPointer) {
      anchorX = this._lastPointer.x;
      anchorY = this._lastPointer.y;
    } else if (this._currentTarget) {
      // Focus / no cursor — anchor near the focused element.
      const rect = this._currentTarget.getBoundingClientRect();
      anchorX = rect.right;
      anchorY = rect.bottom;
    } else {
      return;
    }

    // Default: right and slightly below.
    let left = anchorX + CURSOR_OFFSET_X;
    let top = anchorY + CURSOR_OFFSET_Y;

    // Flip to left of the anchor if the right side would clip.
    if (left + ttRect.width + VIEWPORT_PADDING > window.innerWidth) {
      left = anchorX - ttRect.width - CURSOR_OFFSET_X;
    }
    // Flip to above the anchor if the bottom would clip.
    if (top + ttRect.height + VIEWPORT_PADDING > window.innerHeight) {
      top = anchorY - ttRect.height - CURSOR_OFFSET_Y;
    }

    // Final viewport clamp.
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - ttRect.width - VIEWPORT_PADDING),
    );
    top = Math.max(
      VIEWPORT_PADDING,
      Math.min(top, window.innerHeight - ttRect.height - VIEWPORT_PADDING),
    );
    tt.style.left = `${left}px`;
    tt.style.top = `${top}px`;
  }
}

LubooTooltip.install();

export { LubooTooltip };
